import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ApolloQueryResult } from '@apollo/client';
import {
  CapacityReportOrderByInput,
  CapacityReportStatus,
  FetchCapacityReportArgs,
  GetCapacityReportsCountGQL,
  GetCapacityReportsCountQuery,
  GetCapacityReportsGQL,
  GetCapacityReportsQuery,
  Sort,
} from '@libs/services/graphql/generated/ala-backend.service';
import { Mutable } from '@libs/services/graphql/utilities/to-mutable';
import { NotificationService } from '@libs/services/notification.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { tap } from 'rxjs';
import { constants } from '../../../../shared/constants/constants';
import { ViewEntriesComponent } from '../../components/view-entries/view-entries.component';
import { CapacityReportVM } from '../../viewmodels/facility.viewmodel';

@UntilDestroy()
@Component({
  selector: 'app-view-entries-page',
  imports: [CommonModule, ViewEntriesComponent],
  templateUrl: './view-entries-page.component.html',
  styleUrl: './view-entries-page.component.scss',
})
export class ViewEntriesPageComponent implements OnInit {
  constructor() {
    inject(DestroyRef).onDestroy(() => {
      if (this.timeoutId) {
        this.timeoutId.forEach((id) => {
          clearTimeout(id);
        });
      }
    });
  }
  router = inject(Router);
  notificationService = inject(NotificationService);
  getCapacityReportsGQL = inject(GetCapacityReportsGQL);
  getCapacityReportsCountGQL = inject(GetCapacityReportsCountGQL);

  filteredCapacityReports = signal<CapacityReportVM[]>([]);
  filteredCapacityReportsCount = signal<number>(0);
  page = signal<number>(1);
  tab = signal<CapacityReportStatus[]>([
    CapacityReportStatus.INPROGRESS,
    CapacityReportStatus.NOTSTARTED,
  ]);
  perPageCount = signal<number>(constants.VIEW_ENTRIES_PERPAGE_COUNT[0]);
  sort = signal<Mutable<CapacityReportOrderByInput>>({
    capacityReportPeriodDueDate: Sort.ASC,
  });
  showProgressIndicator = signal<boolean>(false);

  selectedFacility?: CapacityReportVM;
  timeoutId: number[] = [];

  async ngOnInit(): Promise<void> {
    this.fetchCapacityReportsTotalCount(this.tab());
    this.fetchCapacityReportsOnPageChange({
      page: this.page(),
      tab: this.tab(),
      perPageCount: this.perPageCount(),
      sort: this.sort(),
    });
    this.notificationService.drawerClosed$.subscribe(() => {
      this.drawerClosed();
    });
  }

  fetchCapacityReportsTotalCount(tab: CapacityReportStatus[]) {
    let filter: FetchCapacityReportArgs = {};
    filter = { ...filter, statuses: tab };
    this.getCapacityReportsCountGQL
      .fetch({ fetchCapacityReportArgs: filter }, { fetchPolicy: 'no-cache' })
      .pipe(
        tap(() => {
          // Capacity reports total count logging removed to prevent flaky tests
        }),
        untilDestroyed(this)
      )
      .subscribe({
        next: (result: ApolloQueryResult<GetCapacityReportsCountQuery>) => {
          //console.log('Capacity reports total count2', result.data?.count);
          // if (result.data?.count == 0 && tab === 'All') {
          //   this.router.navigate(['coming-soon']);
          // }
          this.filteredCapacityReportsCount.set(result.data?.count || 0);
        },
        error: (err) => {
          console.error('Error fetching data', err.message);
          if (err.message === 'Unauthorized') {
            this.router.navigate(['unauthorized']);
          } else {
            this.router.navigate(['temporarily-unavailable']);
          }
        },
      });
  }
  fetchCapacityReportsOnPageChange(taborPageChanged: {
    tab: CapacityReportStatus[];
    page: number;
    perPageCount: number;
    sort: Mutable<CapacityReportOrderByInput>;
  }) {
    this.tab.set(taborPageChanged.tab);
    this.page.set(taborPageChanged.page);
    this.perPageCount.set(taborPageChanged.perPageCount);
    this.sort.set(taborPageChanged.sort);
    // console.log(
    //   '[View entries Page] page, tab, perPageCount',
    //   this.page(),
    //   this.tab(),
    //   this.perPageCount()
    // );

    let filter: FetchCapacityReportArgs = {
      skip: (taborPageChanged.page - 1) * taborPageChanged.perPageCount,
      take: taborPageChanged.perPageCount || 10,
      orderBy: taborPageChanged.sort,
    };
    filter = { ...filter, statuses: taborPageChanged.tab };
    if (this.showProgressIndicator()) {
      this.notificationService.isProgressIndicator.set(true);
      this.notificationService.progressIndicatorMessage.set(
        `Loading '${this.formatLabel(
          this.tab().length > 1 ? 'To-do' : this.tab()[0]
        )}' capacity reports...`
      );
      const timeoutId = window.setTimeout(() => {
        if (this.notificationService.isProgressIndicator()) {
          console.error('Request took more time to complete');
          this.router.navigate(['temporarily-unavailable']);
        }
      }, (this.notificationService.timeoutInterval || 3) * 1000);
      this.timeoutId.push(timeoutId);
    }

    this.getCapacityReportsGQL
      .fetch({ fetchCapacityReportArgs: filter }, { fetchPolicy: 'no-cache' })
      .pipe(
        tap(() => {
          // Capacity reports logging removed to prevent flaky tests
        }),

        untilDestroyed(this)
      )
      .subscribe({
        next: (result: ApolloQueryResult<GetCapacityReportsQuery>) => {
          // console.log('Results from API', result);
          if (
            this.showProgressIndicator() &&
            this.notificationService.isProgressIndicator()
          ) {
            this.notificationService.isProgressIndicator.set(false);
            if (this.timeoutId) {
              this.timeoutId.forEach((id) => {
                clearTimeout(id);
              });
            }
          }

          const results: CapacityReportVM[] =
            result.data?.capacityReports
              ?.filter((capacityReport) => capacityReport)
              .map((capacityReport) =>
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                this.capacityReportToViewModel(capacityReport!)
              ) || [];
          // Filtered capacity reports logging - removed to prevent flaky tests
          this.filteredCapacityReports.set(results);
        },
        error: (err) => {
          console.error('Error fetching data', err.message);
          if (
            this.showProgressIndicator() &&
            this.notificationService.isProgressIndicator()
          ) {
            this.notificationService.isProgressIndicator.set(false);
          }

          if (err.message === 'Unauthorized') {
            this.router.navigate(['unauthorized']);
          } else {
            this.router.navigate(['temporarily-unavailable']);
          }
        },
      });
  }
  private capacityReportToViewModel(capacityReport: {
    readonly id?: string | undefined;
    readonly capacityReportPeriodDueDate?: Date | undefined;
    readonly submittedAt?: (Date | null) | undefined;
    readonly facilityName?: string | undefined;
    readonly status?: CapacityReportStatus | undefined;
    readonly submittedByName?: string | null | undefined;
    readonly hasEarlierIncompleteReports?: boolean | undefined;
  }): CapacityReportVM {
    return {
      capacityReportPeriodDueDate: capacityReport.capacityReportPeriodDueDate,
      submittedAt: capacityReport.submittedAt,
      facilityName: capacityReport.facilityName,

      id: capacityReport.id,
      status: capacityReport.status,
      submittedByName: capacityReport.submittedByName,
      hasEarlierIncompleteReports: capacityReport.hasEarlierIncompleteReports,
    } as CapacityReportVM;
  }

  fetchCapacityReportsOnTabChange(taborPageChanged: {
    tab: CapacityReportStatus[];
    page: number;
    perPageCount: number;
    sort: Mutable<CapacityReportOrderByInput>;
  }) {
    this.showProgressIndicator.set(true);
    this.fetchCapacityReportsTotalCount(taborPageChanged.tab);
    this.fetchCapacityReportsOnPageChange({
      tab: taborPageChanged.tab,
      page: taborPageChanged.page,
      perPageCount: taborPageChanged.perPageCount,
      sort: taborPageChanged.sort,
    });
  }
  fetchCapacityReportsOnSortChange(taborPageChanged: {
    tab: CapacityReportStatus[];
    page: number;
    perPageCount: number;
    sort: Mutable<CapacityReportOrderByInput>;
  }) {
    this.showProgressIndicator.set(true);
    this.fetchCapacityReportsTotalCount(taborPageChanged.tab);
    this.fetchCapacityReportsOnPageChange({
      tab: taborPageChanged.tab,
      page: taborPageChanged.page,
      perPageCount: taborPageChanged.perPageCount,
      sort: taborPageChanged.sort,
    });
  }

  fetchCapacityReportsOnPerPageCountChange(taborPageChanged: {
    tab: CapacityReportStatus[];
    page: number;
    perPageCount: number;
    sort: Mutable<CapacityReportOrderByInput>;
  }) {
    this.showProgressIndicator.set(true);
    this.fetchCapacityReportsOnPageChange({
      tab: taborPageChanged.tab,
      page: taborPageChanged.page,
      perPageCount: taborPageChanged.perPageCount,
      sort: taborPageChanged.sort,
    });
  }
  drawerClosed() {
    this.showProgressIndicator.set(false);
    this.fetchCapacityReportsTotalCount(this.tab());
    this.fetchCapacityReportsOnPageChange({
      page: this.page(),
      tab: this.tab(),
      perPageCount: this.perPageCount(),
      sort: this.sort(),
    });
  }
  resetPerPageCount(pageCount: number) {
    this.perPageCount.set(pageCount);
  }
  formatLabel(label: string) {
    const formattedLabel = label.split('').reduce((acc, currVal, currIndex) => {
      if (currVal.toUpperCase() === currVal && currIndex !== 0) {
        return `${acc} ${currVal}`;
      } else {
        return `${acc}${currVal}`;
      }
    });
    return formattedLabel;
  }
}
